import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import {
  Activity,
  Plus,
  Edit,
  Trash2,
  ArrowDown,
  ArrowUp,
  Gavel,
  XCircle,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";

const ACTION_META = {
  created: { icon: Plus, color: "text-[#7BA585]", label: "Created" },
  updated: { icon: Edit, color: "text-[#D4AF37]", label: "Updated" },
  deleted: { icon: Trash2, color: "text-red-400", label: "Deleted" },
  stock_in: { icon: ArrowDown, color: "text-[#7BA585]", label: "Stock In" },
  stock_out: { icon: ArrowUp, color: "text-[#C99356]", label: "Stock Out" },
  sold: { icon: CheckCircle, color: "text-[#7BA585]", label: "Sold" },
  cancelled: { icon: XCircle, color: "text-red-400", label: "Cancelled" },
  reserved: { icon: Gavel, color: "text-[#C99356]", label: "Reserved" },
};

const ENTITIES = ["", "product", "sale", "expense", "customer", "supplier", "auction"];

export default function Audit() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterEntity, setFilterEntity] = useState("");
  const [filterAction, setFilterAction] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    if (filterEntity) params.set("entity", filterEntity);
    if (filterAction) params.set("action", filterAction);
    params.set("limit", "300");
    const [a, s] = await Promise.all([
      api.get(`/audit?${params}`),
      api.get("/audit/summary"),
    ]);
    setItems(a.data);
    setSummary(s.data);
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    /* eslint-disable-next-line */
  }, [filterEntity, filterAction, user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <Layout title="Audit Log" subtitle="House activity ledger">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <div className="glass-card p-7">
            <div className="overline">Total Events</div>
            <div className="serif text-3xl mt-3" data-testid="audit-total">{summary.total}</div>
          </div>
          <div className="glass-card p-7">
            <div className="overline">By Action</div>
            <div className="mt-3 space-y-1.5">
              {summary.by_action.slice(0, 4).map((a) => (
                <div key={a.action} className="flex justify-between text-xs">
                  <span className="text-[#8A8A8A] capitalize">{a.action.replace("_", " ")}</span>
                  <span className="mono text-[#D4AF37]">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-card p-7">
            <div className="overline">By Entity</div>
            <div className="mt-3 space-y-1.5">
              {summary.by_entity.slice(0, 4).map((e) => (
                <div key={e.entity} className="flex justify-between text-xs">
                  <span className="text-[#8A8A8A] capitalize">{e.entity}</span>
                  <span className="mono text-[#D4AF37]">{e.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-card p-7">
            <div className="overline">Most Active</div>
            <div className="mt-3 space-y-1.5">
              {summary.top_users.slice(0, 4).map((u) => (
                <div key={u.user_id} className="flex justify-between text-xs">
                  <span className="text-[#8A8A8A] truncate mr-2">{u.user_name}</span>
                  <span className="mono text-[#D4AF37]">{u.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          data-testid="audit-filter-entity"
          value={filterEntity}
          onChange={(e) => setFilterEntity(e.target.value)}
          className="luxe-input px-4 py-2.5 text-sm"
        >
          {ENTITIES.map((e) => (
            <option key={e} value={e}>{e ? e[0].toUpperCase() + e.slice(1) : "All Entities"}</option>
          ))}
        </select>
        <select
          data-testid="audit-filter-action"
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="luxe-input px-4 py-2.5 text-sm"
        >
          <option value="">All Actions</option>
          {Object.keys(ACTION_META).map((a) => <option key={a} value={a}>{ACTION_META[a].label}</option>)}
        </select>
      </div>

      {/* Timeline */}
      <div className="glass-card p-7 lg:p-10">
        {items.length === 0 ? (
          <div className="text-center py-16 text-[#8A8A8A]">
            <Activity className="w-8 h-8 mx-auto mb-3 text-[#333]" strokeWidth={1} />
            No events recorded.
          </div>
        ) : (
          <div className="space-y-0" data-testid="audit-timeline">
            {items.map((ev, idx) => {
              const meta = ACTION_META[ev.action] || { icon: Activity, color: "text-[#8A8A8A]", label: ev.action };
              const Icon = meta.icon;
              return (
                <div key={ev.id} className="flex gap-5 py-5 border-b border-white/5 last:border-0" data-testid={`audit-row-${idx}`}>
                  <div className="shrink-0 mt-1">
                    <div className={`w-8 h-8 border border-white/10 flex items-center justify-center ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.25} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline flex-wrap gap-2">
                      <span className={`text-xs uppercase tracking-[0.18em] ${meta.color}`}>{meta.label}</span>
                      <span className="text-xs text-[#666]">·</span>
                      <span className="text-xs text-[#8A8A8A] capitalize">{ev.entity}</span>
                    </div>
                    <div className="text-sm mt-1">{ev.summary || ev.entity_id}</div>
                    <div className="flex items-center gap-3 mt-2 text-[0.7rem]">
                      <span className="text-[#666]">by</span>
                      <span className="text-[#D4AF37]">{ev.user_name}</span>
                      <span className="text-[#666] mono">·</span>
                      <span className="text-[#666] mono">{ev.user_role}</span>
                    </div>
                  </div>
                  <div className="shrink-0 mono text-[0.65rem] text-[#666] text-right whitespace-nowrap">
                    <div>{ev.created_at?.slice(0, 10)}</div>
                    <div>{ev.created_at?.slice(11, 19)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
